import React, { useEffect, useState } from 'react';
import { commonService } from '../../services/commonService';
import '../../assets/styles/Workflow.css';

interface SendDeliverModalProps {
  item: any;
  /** True if this exact round has already been sent at least once - relabels the modal as a resend instead of a first-time send. */
  isResend?: boolean;
  /** True if this is a partial-batch delivery rather than the final one - only affects wording, the send call itself is identical either way. */
  isPartial?: boolean;
  onClose: () => void;
  onSent: () => void;
}

const buildDefaultSubject = (item: any) => `Your ${item?.brand_name || ''} data delivery is ready`;

// PC Manager never opens this modal, or delivers at all - Fulfillment owns
// every send, partial or final, and always lands here with full
// contact-detail fields to fill in and send themselves.
const SendDeliverModal: React.FC<SendDeliverModalProps> = ({ item, isResend, isPartial, onClose, onSent }) => {
  const [to, setTo] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState(buildDefaultSubject(item));

  const [loadingLead, setLoadingLead] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadLead = async () => {
      setLoadingLead(true);
      try {
        const response = await commonService.fdcGetLead(item?.projectid);
        if (!active) return;

        setTo(response?.message?.Email || '');
        setFullName(response?.message?.Name || '');
        setPhone(response?.message?.Phone || '');
        setSubject(response?.message?.Subject || buildDefaultSubject(item));
      } catch (err) {
        // API failed -> open with empty fields, same fallback as onChangeAction's "send" branch,
        // but default the subject the same way a successful fetch would.
        if (!active) return;

        setTo('');
        setFullName('');
        setPhone('');
        setSubject(buildDefaultSubject(item));
      } finally {
        if (active) setLoadingLead(false);
      }
    };

    loadLead();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.projectid]);

  const handleSend = async () => {
    if (!to.trim() || !subject.trim() || !fullName.trim()) {
      setError('To, Subject, and Full Name are required');
      return;
    }

    setError(null);
    try {
      setSubmitting(true);
      await commonService.fdcSendEmail({
        project_id: item?.projectid,
        customer_email: to,
        subject,
        name: fullName,
        phone,
      });

      onSent();
      onClose();
    } catch (err) {
      setError('Failed to send email');
    } finally {
      setSubmitting(false);
    }
  };

  const initial = fullName.trim() ? fullName.trim().charAt(0).toUpperCase() : '✉';

  return (
    <div className="wf-overlay">
      <div className="wf-modal" style={{ maxWidth: 620 }}>
        <div className="wf-modal-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="wf-senddeliver-icon">📤</div>
            <div>
              <h2 style={{ fontSize: 16.5, margin: 0 }}>
                {isResend
                  ? (isPartial ? 'Resend Partial Delivery' : 'Resend to Customer')
                  : (isPartial ? 'Send Partial Delivery' : 'Send / Deliver')}
              </h2>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                {item?.brand_name || 'Project'}{item?.projectid ? ` · ${item.projectid}` : ''}
              </div>
            </div>
          </div>
          <button className="wf-iconbtn" onClick={onClose}>✕</button>
        </div>

        <div className="wf-modal-body">
          {error && (
            <div className="wf-senddeliver-error">
              <span>⚠</span>{error}
            </div>
          )}

          <div className="wf-stack wf-form">
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14 }}>
              <div className="wf-senddeliver-avatar">{loadingLead ? '…' : initial}</div>
              <div style={{ flex: 1 }}>
                <label className="wf-field-label">Recipient email<span style={{ color: '#ef4444' }}> *</span></label>
                <input
                  type="email"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  disabled={loadingLead}
                />
              </div>
            </div>

            <div className="wf-grid2">
              <div>
                <label className="wf-field-label">Full name<span style={{ color: '#ef4444' }}> *</span></label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={loadingLead}
                />
              </div>
              <div>
                <label className="wf-field-label">Phone</label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={loadingLead}
                />
              </div>
            </div>

            <div>
              <label className="wf-field-label">Subject<span style={{ color: '#ef4444' }}> *</span></label>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={loadingLead} />
            </div>
          </div>
        </div>

        <div className="wf-modal-foot" style={{ justifyContent: 'flex-end', gap: 10 }}>
          <button className="wf-btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            className="wf-btn wf-btn-primary"
            disabled={!to.trim() || !subject.trim() || !fullName.trim() || submitting || loadingLead}
            onClick={handleSend}
          >
            {submitting ? (isResend ? 'Resending…' : 'Sending…') : (isResend ? '↻ Resend' : '➤ Send')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendDeliverModal;
